import React from 'react';
import {
  Button,
  Flex,
  TagBadge,
  Text,
  Tooltip,
  TooltipTrigger,
  StepsPreviewSkeleton,
} from '@chatsdet/ui';
import { NODE_TYPE_LABEL } from '@chatsdet/types';
import { topoOrderNodes } from '@chatsdet/workflow-diff';
import {
  SignIn,
  Globe,
  CursorClick,
  CheckCircle,
  Gear,
  Clock,
  Plant,
  ArrowRight,
  ArrowSquareOut,
  DotsThree,
  type Icon,
} from '@phosphor-icons/react';
import type {
  WorkflowListItemWithDetails,
  WorkflowNode,
  WorkflowNodeType,
} from '../../../hooks/useWorkflows.js';
import { useWorkflowDetail } from '../../../hooks/useWorkflows.js';
import type { Tag, TagColor } from '../../../hooks/useTags.js';

const MAX_VISIBLE_NODES = 8;

type NodeTypeConfig = {
  icon: Icon;
  label: string;
  bgColor: string;
  textColor: string;
  borderColor: string;
  iconBg: string;
};

const NODE_TYPE_CONFIG: Record<WorkflowNodeType, NodeTypeConfig> = {
  login: {
    icon: SignIn,
    label: NODE_TYPE_LABEL.login,
    bgColor: 'bg-amber-100 dark:bg-amber-900/40',
    textColor: 'text-amber-800 dark:text-amber-300',
    borderColor: 'border-amber-300 dark:border-amber-700',
    iconBg: 'bg-amber-200 dark:bg-amber-800',
  },
  page: {
    icon: Globe,
    label: 'Navigate',
    bgColor: 'bg-blue-100 dark:bg-blue-900/40',
    textColor: 'text-blue-800 dark:text-blue-300',
    borderColor: 'border-blue-300 dark:border-blue-700',
    iconBg: 'bg-blue-200 dark:bg-blue-800',
  },
  action: {
    icon: CursorClick,
    label: 'Action',
    bgColor: 'bg-emerald-100 dark:bg-emerald-900/40',
    textColor: 'text-emerald-800 dark:text-emerald-300',
    borderColor: 'border-emerald-300 dark:border-emerald-700',
    iconBg: 'bg-emerald-200 dark:bg-emerald-800',
  },
  assertion: {
    icon: CheckCircle,
    label: 'Assert',
    bgColor: 'bg-violet-100 dark:bg-violet-900/40',
    textColor: 'text-violet-800 dark:text-violet-300',
    borderColor: 'border-violet-300 dark:border-violet-700',
    iconBg: 'bg-violet-200 dark:bg-violet-800',
  },
  setup: {
    icon: Gear,
    label: 'Setup',
    bgColor: 'bg-slate-100 dark:bg-slate-800/40',
    textColor: 'text-slate-700 dark:text-slate-300',
    borderColor: 'border-slate-300 dark:border-slate-600',
    iconBg: 'bg-slate-200 dark:bg-slate-700',
  },
  wait: {
    icon: Clock,
    label: 'Wait',
    bgColor: 'bg-orange-100 dark:bg-orange-900/40',
    textColor: 'text-orange-800 dark:text-orange-300',
    borderColor: 'border-orange-300 dark:border-orange-700',
    iconBg: 'bg-orange-200 dark:bg-orange-800',
  },
  seed: {
    icon: Plant,
    label: 'Seed',
    bgColor: 'bg-teal-100 dark:bg-teal-900/40',
    textColor: 'text-teal-800 dark:text-teal-300',
    borderColor: 'border-teal-300 dark:border-teal-700',
    iconBg: 'bg-teal-200 dark:bg-teal-800',
  },
};

function truncate(text: string, maxLength: number): string {
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength - 1) + '…';
}

type StepNodeProps = {
  node: WorkflowNode;
  isLast: boolean;
};

function getStepDescription(node: WorkflowNode): string | null {
  const config = node.configJson;
  if (!config) return null;

  // Fields vary by node type - check all possible descriptive fields
  // Navigate nodes
  if (config.pageTitle) return config.pageTitle as string;
  if (config.pageUrl) return config.pageUrl as string;

  // Action nodes
  if (config.actionTitle) return config.actionTitle as string;
  if (config.actionDescription) return config.actionDescription as string;

  // Assertion nodes
  if (config.condition) return config.condition as string;

  // Login nodes
  if (config.credentialName) return config.credentialName as string;

  // Wait nodes
  if (config.mode === 'condition') {
    if (typeof config.passCondition === 'string' && config.passCondition.trim()) {
      return truncate(config.passCondition.trim(), 28);
    }
    return 'Until ready';
  }
  if (config.delaySeconds) return `${config.delaySeconds}s`;

  // Generic fallbacks
  if (config.label) return config.label as string;
  if (config.description) return config.description as string;

  return null;
}

function StepNode({ node, isLast }: StepNodeProps) {
  const config = NODE_TYPE_CONFIG[node.nodeType] ?? NODE_TYPE_CONFIG.action;
  const IconComponent = config.icon;
  const description = getStepDescription(node);
  const displayDescription = description ? truncate(description, 28) : null;

  return (
    <div className="flex items-center shrink-0">
      <TooltipTrigger delay={200}>
        <div
          className={`
            flex items-center gap-2 pl-1.5 pr-3 py-1.5 rounded-lg border
            ${config.bgColor} ${config.borderColor}
            cursor-default transition-all hover:shadow-sm
          `}
        >
          <div className={`p-1.5 rounded-md ${config.iconBg}`}>
            <IconComponent size={14} weight="bold" className={config.textColor} />
          </div>
          <div className="flex flex-col min-w-0">
            <span
              className={`text-[10px] font-semibold uppercase tracking-wide ${config.textColor} opacity-70`}
            >
              {config.label}
            </span>
            {displayDescription && (
              <span className="text-xs font-medium text-foreground whitespace-nowrap truncate max-w-[180px]">
                {displayDescription}
              </span>
            )}
          </div>
        </div>
        <Tooltip variant="light">
          <div className="max-w-sm">
            <Text size="xs" weight="medium" className="text-foreground">
              {config.label}
            </Text>
            {description && (
              <Text size="xs" className="text-muted-foreground mt-1 break-words">
                {description}
              </Text>
            )}
          </div>
        </Tooltip>
      </TooltipTrigger>
      {!isLast && (
        <div className="flex items-center mx-1.5">
          <div className="w-4 h-px bg-border" />
          <ArrowRight size={12} className="text-muted-foreground/50 -ml-0.5" />
        </div>
      )}
    </div>
  );
}

type MoreNodesIndicatorProps = {
  remainingCount: number;
  remainingNodes: WorkflowNode[];
};

function MoreNodesIndicator({ remainingCount, remainingNodes }: MoreNodesIndicatorProps) {
  // Build summary of remaining node types
  const typeSummary = React.useMemo(() => {
    const counts: Record<string, number> = {};
    for (const node of remainingNodes) {
      counts[node.nodeType] = (counts[node.nodeType] || 0) + 1;
    }
    return Object.entries(counts)
      .map(([type, count]) => {
        const config = NODE_TYPE_CONFIG[type as WorkflowNodeType];
        return `${count} ${config?.label?.toLowerCase() ?? type}`;
      })
      .join(', ');
  }, [remainingNodes]);

  return (
    <div className="flex items-center shrink-0">
      <div className="flex items-center mx-1">
        <div className="w-3 h-px bg-border" />
        <ArrowRight size={12} className="text-muted-foreground/60 -ml-0.5" />
      </div>
      <TooltipTrigger delay={200}>
        <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-muted border border-border cursor-default">
          <DotsThree size={14} weight="bold" className="text-muted-foreground" />
          <span className="text-xs font-medium text-muted-foreground">+{remainingCount} more</span>
        </div>
        <Tooltip>
          <Text size="xs" className="text-inherit">
            {typeSummary}
          </Text>
        </Tooltip>
      </TooltipTrigger>
    </div>
  );
}

type HorizontalStepsFlowProps = {
  nodes: WorkflowNode[];
  edges: Array<{ sourceNodeId: string; targetNodeId: string }>;
  totalCount: number;
};

export function HorizontalStepsFlow({ nodes, edges, totalCount }: HorizontalStepsFlowProps) {
  if (nodes.length === 0) {
    return (
      <Text size="sm" className="text-muted-foreground italic">
        No steps defined
      </Text>
    );
  }

  // Order nodes by flow (edge) order — orderIndex is deprecated.
  const sortedNodes = topoOrderNodes(nodes, edges);

  // Show up to MAX_VISIBLE_NODES, with a "+N more" indicator if truncated
  const visibleNodes = sortedNodes.slice(0, MAX_VISIBLE_NODES);
  const remainingNodes = sortedNodes.slice(MAX_VISIBLE_NODES);
  const remainingCount = totalCount - MAX_VISIBLE_NODES;
  const hasMore = remainingCount > 0;

  return (
    <div>
      <div className="flex items-center w-max py-1">
        {visibleNodes.map((node, index) => (
          <StepNode
            key={node.id}
            node={node}
            isLast={!hasMore && index === visibleNodes.length - 1}
          />
        ))}
        {hasMore && (
          <MoreNodesIndicator remainingCount={remainingCount} remainingNodes={remainingNodes} />
        )}
      </div>
    </div>
  );
}

type WorkflowTagsProps = {
  tags: Tag[];
};

function WorkflowTags({ tags }: WorkflowTagsProps) {
  if (tags.length === 0) return null;

  return (
    <Flex gap="xs" wrap align="center">
      {tags.map((tag) => (
        <TagBadge key={tag.id} name={tag.name} color={tag.color as TagColor} size="sm" />
      ))}
    </Flex>
  );
}

type WorkflowStepsPreviewProps = {
  workflow: WorkflowListItemWithDetails;
  onNavigateToWorkflow: () => void;
};

/**
 * Workflow steps preview panel - displayed when a table row is expanded.
 * Fetches workflow details (nodes) on-demand when rendered.
 */
export function WorkflowStepsPreview({
  workflow,
  onNavigateToWorkflow,
}: WorkflowStepsPreviewProps) {
  const { tags } = workflow;

  // Fetch workflow detail on-demand (lazy loading)
  const { data: workflowDetail, isLoading } = useWorkflowDetail(workflow.id);

  // Show skeleton while loading
  if (isLoading) {
    return <StepsPreviewSkeleton stepCount={5} />;
  }

  const nodes = workflowDetail?.nodes ?? [];
  const edges = workflowDetail?.edges ?? [];
  const nodeCount = nodes.length;

  return (
    <div className="py-4 px-6 bg-muted/20 border-t border-border/50 max-w-full overflow-hidden">
      {/* Header with button */}
      <div className="flex items-center justify-between gap-4 mb-3">
        <div className="w-0 flex-grow overflow-x-auto overflow-y-hidden">
          {/* Steps visualization - scrollable horizontally only */}
          <HorizontalStepsFlow nodes={nodes} edges={edges} totalCount={nodeCount} />
        </div>

        <Button
          variant="ghost"
          size="sm"
          onPress={onNavigateToWorkflow}
          iconRight={<ArrowSquareOut size={14} />}
          className="shrink-0"
        >
          Open editor
        </Button>
      </div>

      {/* Tags - no label needed */}
      {tags.length > 0 && (
        <div className="pt-3 border-t border-border/30">
          <WorkflowTags tags={tags} />
        </div>
      )}
    </div>
  );
}
